import React, { useCallback, useEffect, useState } from "react";
import useTranslation from "next-translate/useTranslation";
import debounce from "lodash.debounce";
import { gaEvent } from "../../lib/ga";
import {
  validateEmail,
  validatePassword,
  validateUsername,
} from "../../util/validate";
import RegisterError from "../../components/RegisterError";
import AppleButton from "../../components/AppleButton";
import styles from "./styles/Register.module.css";
import Script from "next/script";
import { setLoadingBar } from "../../ducks/siteData";
import { useRouter } from "next/router";
import { api } from "../../services/api";
import { errorCodes } from "@chesshotel/constants";
import { generateId } from "../../util/generateId";
import AllMedals from "../../components/AllMedals";
import { useSize } from "../../hooks/useSize";
import { useRegister } from "../../ducks/register";
import { useLeagues } from "../../ducks/leagues";
import { setUserData } from "../../ducks/signIn";
import { GamePlayWs } from "../../services/wsOutRoutes";
import FacebookButton from "@/src/components/FacebookButton";
import { signInFB } from "@/src/services/facebook";
import { signInAppleWeb } from "../../services/apple";
import FBScript from "@/src/components/FBScript";

// let mouseDown = false;
const failStyle = { border: "1px solid #ff4900" };

export default function Register() {
  const { leagueScores, hasMedals } = useLeagues();

  useEffect(() => {
    if (hasMedals) {
      GamePlayWs.saveProgress();
    }
  }, []);

  const { t } = useTranslation("common");
  const { t: registerT } = useTranslation("auth");
  const isDesktop = useSize();

  // const [country, setCountry] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [news, setNews] = useState(0);
  // const [confirmPassword, setConfirmPassword] = useState("");

  const [usernameError, setUsernameError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  // const [confirmPasswordError, setConfirmPasswordError] = useState(null);
  // const [countryError, setCountryError] = useState(null);

  const router = useRouter();
  const { guestUsername, guestElo, progressKey } = useRegister();

  async function validateCreateAccount() {
    const passwordErr = validatePassword(password);
    // const confirmPasswordErr = validateConfirmPassword(
    //   password,
    //   confirmPassword
    // );
    const emailErr = await validateEmail(email);
    const usernameErr = await validateUsername(username, router.locale);
    // const countryErr = validateCountry(country);

    setPasswordError(passwordErr);
    // setConfirmPasswordError(confirmPasswordErr);
    setEmailError(emailErr);
    setUsernameError(usernameErr);
    // setCountryError(countryErr);

    if (
      !passwordErr &&
      // !confirmPasswordErr &&
      !emailErr &&
      !usernameErr
      // &&
      // !countryErr
    ) {
      createAccountRequest();
    }
  }

  function createAccountRequest() {
    setLoadingBar(true);

    const lang = router.locale;
    const elo = guestElo;
    // gaEvent('RegisterRequest', 'Register Email', username);
    const buildTarget = "web";

    api
      .post(
        "/users",
        {
          username,
          email,
          password,
          lang,
          country: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
          elo,
          news,
          buildTarget,
          progressKey,
        },
        {
          validateStatus: function (status) {
            return status < 400; // Resolve only if the status code is less than 400
          },
        }
      )
      .then((response) => {
        if (response.data) {
          const { user } = response.data;
          router.push(
            `/register/almostDone?email=${user.email}&userId=${user.id}`
          );
          gaEvent({ category: "RegisterWithEmail", action: "CreateAccount" });

          setUserData(response.data);
        } else {
          setUsernameError(errorCodes.ERROR_GENERAL);
        }
      })
      .catch((error) => {
        if (error.response.data === errorCodes.ERROR_USERNAME_NOT_AVAILABLE) {
          setUsernameError(errorCodes.ERROR_USERNAME_NOT_AVAILABLE);
        } else if (
          error.response.data === errorCodes.ERROR_EMAIL_NOT_AVAILABLE
        ) {
          setEmailError(errorCodes.ERROR_EMAIL_NOT_AVAILABLE);
        } else {
          setUsernameError(errorCodes.ERROR_GENERAL);
        }
      })
      .then(() => {
        setLoadingBar(false);
      });
  }

  useEffect(() => {
    if (guestUsername) {
      validateUsername(guestUsername, router.locale);
    }
    gaEvent({ category: "RegisterWithEmail", action: "StartCreateAccount" });
  }, []);

  function renderElo() {
    return (
      <div className={styles.elo}>
        <label className={styles.label}>{t("Rating")}</label>
        <input disabled type="text" value={guestElo} />
      </div>
    );
  }

  // function renderCountries() {
  //   const codes = Object.keys(isoCountries);
  //   return codes.map((code) => {
  //     const country = isoCountries[code];
  //     return (
  //       <option key={code} value={code}>
  //         {country}
  //       </option>
  //     );
  //   });
  // }

  const debouncedValidateEmail = useCallback(
    debounce(async (email) => {
      setEmailError(await validateEmail(email));
    }, 700),
    []
  );

  async function emailChange(email: string) {
    setEmail(email);
    debouncedValidateEmail(email);
  }

  // async function emailChange(email: string) {
  //   if (emailError) {
  //     setEmailError(await validateEmail(email));
  //   }
  //   setEmail(email);
  // }

  const debouncedValidateUsername = useCallback(
    debounce(async (username) => {
      setUsernameError(await validateUsername(username, router.locale));
    }, 700),
    []
  );

  async function usernameChange(username: string) {
    setUsername(username);
    debouncedValidateUsername(username);
  }

  // async function usernameChange(username: string) {
  //   if (username.length > 4 || usernameError) {
  //     setUsernameError(await validateUsername(username, router.locale));
  //   }
  //   setUsername(username);
  // }

  const debouncedValidatePassword = useCallback(
    debounce(async (password) => {
      setPasswordError(validatePassword(password));
    }, 700),
    []
  );

  function passwordChange(password: string) {
    setPassword(password);
    debouncedValidatePassword(password);
  }

  // function confirmPasswordChange(password: string) {
  //   if (confirmPasswordError) {
  //     setConfirmPasswordError(validatePassword(password));
  //   }

  //   setConfirmPassword(password);
  // }

  const usernameStyle = usernameError && failStyle;
  const emailStyle = emailError && failStyle;
  const passwordStyle = passwordError && failStyle;
  // const confirmPasswordStyle = confirmPasswordError && failStyle;
  // const countryStyle = countryError && failStyle;

  return (
    <div className={styles.body}>
      <FBScript />
      <Script
        onLoad={() => {
          //@ts-ignore
          window.AppleID.auth.init({
            clientId: "com.chesshotel.service",
            scope: "",
            redirectURI:
              "https://www.chesshotel.com/api/v1/auth/apple/redirect",
            state: "Eksätravägen975031",
            nonce: generateId(),
            usePopup: true, // or false defaults to false
          });
        }}
        src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
      />

      {hasMedals && (
        <div className="flex items-center justify-center w-full pb-6">
          <AllMedals
            medalData={leagueScores}
            size={isDesktop ? "medium" : "small"}
          />
        </div>
      )}

      {guestElo && renderElo()}
      <FacebookButton
        onClick={() => signInFB()}
        text="Create account with Facebook"
      />
      <div className={styles.appleBtn}>
        <AppleButton
          onClick={() => signInAppleWeb()}
          text="Create account with Apple"
        />
      </div>
      <div className={styles.line}>
        <span>{registerT("or")}</span>
      </div>
      <label htmlFor="username" className={styles.label}>
        {registerT("Username")}
      </label>
      <input
        autoFocus={guestElo === null}
        type="text"
        id="username"
        style={usernameStyle}
        value={username}
        onChange={(e) => usernameChange(e.target.value)}
        // onBlur={async () =>
        //   !mouseDown &&
        //   username &&
        //   setUsernameError(await validateUsername(username, router.locale))
        // }
      />
      {usernameError && <RegisterError errorCode={usernameError} />}

      <label htmlFor="email" className={styles.label}>
        {t("Email")}
      </label>
      <input
        id="email"
        type="email"
        style={emailStyle}
        onChange={(e) => emailChange(e.target.value)}
        // onBlur={async () =>
        //   !mouseDown && email && setEmailError(await validateEmail(email))
        // }
      />
      {emailError && <RegisterError errorCode={emailError} />}
      {/* <div className={styles.thirdParty}>*{registerT("thirdParty")}</div> */}

      <label htmlFor="password" className={styles.label}>
        {t("Password")}
      </label>
      <input
        id="password"
        type="password"
        style={passwordStyle}
        onChange={(e) => passwordChange(e.target.value)}
        // onBlur={() =>
        //   !mouseDown &&
        //   password != null &&
        //   setPasswordError(validatePassword(password))
        // }
      />
      {passwordError && <RegisterError errorCode={passwordError} />}

      {/* <label htmlFor="passwordAgain" className={styles.label}>
        {t("Password again")}
      </label>
      <input
        id="passwordAgain"
        type="password"
        style={confirmPasswordStyle}
        onChange={(e) => confirmPasswordChange(e.target.value)}
      />
      {confirmPasswordError && (
        <RegisterError errorCode={confirmPasswordError} />
      )} */}

      {/* <label htmlFor="country" className={styles.label}>
        {t("Country")}
      </label>
      <select
        id="country"
        style={countryStyle}
        onChange={(e) => setCountry(e.target.value)}
      >
        <option value="">{registerT("Select country")}</option>'
        {renderCountries()}
      </select>
      {countryError && <RegisterError errorCode={countryError} />} */}
      {/* <input
        type="hidden"
        name="country"
        value={Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone}
      /> */}

      <div className={styles.news}>
        <input
          onChange={(e) => setNews(e.target.checked ? 1 : 0)}
          name="news"
          id="newsletter"
          type="checkbox"
          className="cursor-pointer"
        />
        <label htmlFor="newsletter" className="cursor-pointer select-none">
          {registerT("Get free chess lessons and promotions")}
        </label>
      </div>

      <button
        // onMouseDown={() => (mouseDown = true)}
        // onMouseUp={() => (mouseDown = false)}
        className={styles.registerButton}
        onClick={() => validateCreateAccount()}
      >
        {t("Sign up")}
      </button>
    </div>
  );
}
